// Admin API client. Separate session from the public web app — admin tokens
// live in their own localStorage keys (so a non-admin web session can't be
// confused for an admin session).
//
// Endpoints are Convex httpActions (per-page modules in
// src/convex/functions/admin/*), served from the deployment's
// `.convex.site` URL.

import {
  activeConfig,
  getActiveEnv,
  tokenKeyFor,
  expKeyFor,
  meKeyFor,
  ENVIRONMENTS,
  type AdminEnv,
} from './environments';

// Resolved at runtime from the active environment — must switch in
// lockstep with the Convex client's URL, since .convex.site and
// .convex.cloud are the same deployment.
const API_BASE = activeConfig().apiBase;

export class APIError extends Error {
  constructor(public status: number, public code: string, message: string) { super(message); }
}

export interface Admin {
  id: string;
  email: string;
  role: 'admin' | 'moderator';
}

// One row of the Admins management page (api.admin.users.listAdmins).
export interface AdminRow {
  id: string;
  email: string;
  role: 'admin' | 'moderator';
  created_at: number;
  last_seen_at: number | null;
  totp_enrolled: boolean;
}

// Successful sign-in / enrollment → a live session.
export interface AdminSession {
  access: string;
  expires_at: string;
  admin: Admin;
}

// First sign-in (no authenticator enrolled yet): the server returns the
// QR enrollment challenge instead of a session. The SPA shows the QR,
// then calls enrollTotp to confirm a code and obtain a real session.
export interface AdminEnrollmentChallenge {
  enrollment_required: true;
  totp_uri: string;
  secret: string;
}

export type AdminSigninResult = AdminSession | AdminEnrollmentChallenge;

// One-time cleanup of the pre-namespacing keys. Those tokens are dead
// regardless (the RS256 migration changed `iss`), so this only stops
// them lingering in localStorage forever.
function purgeLegacyKeys() {
  try {
    ['wydm.admin.token', 'wydm.admin.expires', 'wydm.admin.me'].forEach((k) =>
      localStorage.removeItem(k),
    );
  } catch {
    /* private mode / disabled storage — nothing to clean */
  }
}
purgeLegacyKeys();

export const adminSession = {
  getToken(env: AdminEnv = getActiveEnv()): string | null { return localStorage.getItem(tokenKeyFor(env)); },
  getExpiresAt(env: AdminEnv = getActiveEnv()): Date | null { const v = localStorage.getItem(expKeyFor(env)); return v ? new Date(v) : null; },
  getMe(env: AdminEnv = getActiveEnv()): Admin | null { const v = localStorage.getItem(meKeyFor(env)); return v ? (JSON.parse(v) as Admin) : null; },
  set(token: string, expiresAt: string, me: Admin, env: AdminEnv = getActiveEnv()) {
    localStorage.setItem(tokenKeyFor(env), token);
    localStorage.setItem(expKeyFor(env), expiresAt);
    localStorage.setItem(meKeyFor(env), JSON.stringify(me));
  },
  clear(env: AdminEnv = getActiveEnv()) {
    localStorage.removeItem(tokenKeyFor(env));
    localStorage.removeItem(expKeyFor(env));
    localStorage.removeItem(meKeyFor(env));
  },
  /**
   * Clear every environment's session. Use for explicit user intent —
   * the "Sign out" button and the idle auto-logout — where the user means
   * "I am leaving", not "drop this one backend".
   *
   * Distinct from clear(env), which stays per-environment on purpose: a
   * heartbeat failing for dev must drop dev's session without touching
   * prod's.
   */
  clearAll() {
    (Object.keys(ENVIRONMENTS) as AdminEnv[]).forEach((env) => this.clear(env));
  },
  isValid(env: AdminEnv = getActiveEnv()): boolean {
    const exp = this.getExpiresAt(env);
    return !!exp && exp > new Date();
  },
};

// Which environment a given apiBase belongs to, so a call pinned to one
// environment's base uses that environment's token — never the active
// env's token sent to a different deployment. Unrecognised/absent base
// falls back to the active environment (the pre-existing default).
function envForBase(base: string | undefined): AdminEnv {
  if (!base) return getActiveEnv();
  const match = (Object.entries(ENVIRONMENTS) as [AdminEnv, { apiBase: string }][]).find(
    ([, cfg]) => cfg.apiBase === base,
  );
  return match ? match[0] : getActiveEnv();
}

async function request<T>(path: string, opts: { method?: string; body?: unknown; base?: string } = {}): Promise<T> {
  const token = adminSession.getToken(envForBase(opts.base));
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch((opts.base ?? API_BASE) + path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = data?.error ?? {};
    throw new APIError(res.status, err.code ?? 'UNKNOWN', err.message ?? 'Request failed');
  }
  return data as T;
}

export type EntitlementTier = 'free' | 'premium' | 'ultra';
export type EntitlementPlan = 'none' | 'weekly' | 'monthly' | 'yearly';

export interface UserRow {
  id: string;
  email: string;
  display_name?: string;
  auth_method: 'email' | 'apple' | 'google';
  profile_complete: boolean;
  email_verified: boolean;
  terms_accepted: boolean;
  online: boolean;
  status: 'active' | 'suspended' | 'soft_deleted' | 'hard_deleted';
  tier: EntitlementTier;
  plan: EntitlementPlan;
  created_at: string;
  last_seen_at?: string | null;
  /**
   * Present only on accounts created through the /admin/v1 API. Console-
   * only: the consumer app has no equivalent, and to another user a
   * synthetic account is indistinguishable from a real one.
   */
  synthetic?: {
    created_by_admin_id: string;
    created_at: string;
    note?: string;
  };
}

export interface ListUsersResponse {
  users: UserRow[];
  total: number;
  page: number;
  page_size: number;
}

// UserDetail is the admin's deep view fed by GET /admin/users/{id}.
// Wider than UserRow: includes the profile slice + aggregates +
// recent subscriptions. Used by /dashboard/users/:id.
export interface UserDetail extends UserRow {
  profile?: AdminProfileSlice;
  ratings_received: number;
  open_reports_count: number;
  subscriptions: AdminSubSlice[];
}

export interface AdminProfileSlice {
  id: string;
  slug?: string;
  bio?: string;
  gender?: string;
  looking_for: string[];
  visibility: 'public' | 'unlisted' | 'private' | string;
  published: boolean;
  published_at?: string;
  photo_count: number;
  updated_at: string;
}

export interface AdminSubSlice {
  id: string;
  vendor: 'stripe' | 'revenuecat' | string;
  vendor_subscription_id: string;
  status: 'active' | 'past_due' | 'cancelled' | 'expired' | string;
  price_id: string;
  is_lifetime: boolean;
  current_period_end?: string;
  cancel_at?: string;
  created_at: string;
}

// AdminPayment — one row of the payments ledger. status values:
//   'succeeded'           — paid, no refund issued
//   'partially_refunded'  — refunded_amount > 0 but < amount
//   'refunded'            — refunded_amount === amount
//   'failed'              — charge failed (recorded for audit)
export interface AdminPayment {
  id: string;
  user_id?: string;
  user_email?: string;
  vendor: 'stripe' | 'revenuecat';
  vendor_payment_id: string;
  vendor_customer_id?: string;
  amount: number;            // minor unit (cents)
  refunded_amount: number;   // minor unit (cents)
  currency: string;
  status: 'succeeded' | 'refunded' | 'partially_refunded' | 'failed';
  description?: string;
  receipt_url?: string;
  created_at: string;
}

export interface PaymentsList {
  payments: AdminPayment[];
  total: number;
  page: number;
  page_size: number;
}

// FeatureFlag — one row of /admin/flags. The `scope` distinguishes
// system-wide flags (e.g. signups gate; applies to everyone) from
// user-scoped flags (test accounts always read ENABLED regardless of
// the persisted value, so admins can dogfood without launching).
export interface FeatureFlag {
  key: string;
  label: string;
  description: string;
  scope: 'system' | 'user';
  default: boolean;
  enabled: boolean;
}

// Hinge-style profile prompts — admin-curated catalog. Categories
// are locked at the schema layer (see backend ProfilePromptCategory
// literal union); adding a new category requires a code change.
export type PromptCategory = 'story_time' | 'lets_chat_about' | 'voice_first';
export interface AdminPrompt {
  id: string;
  category: PromptCategory;
  text: string;
  active: boolean;
  createdAt: number;
}

// Global admin-action audit feed (everything any admin did, regardless
// of which user it touched). Sibling to UserAuditEntry but always has
// an admin actor and may target any resource type.
export interface AdminAuditEntry {
  id: string;
  created_at: string;
  action: string;
  actor_admin_id: string;
  actor_email?: string;
  target_type?: string;
  target_id?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  ip?: string;
  user_agent?: string;
}

export interface AdminAuditFeed {
  entries: AdminAuditEntry[];
  total: number;
  page: number;
  page_size: number;
}

// Per-user audit trail — unified feed combining things the user did
// (actor_role = "user") with things admins did to them (actor_role =
// "admin"). One row per authed mutation, captured by the audit
// middleware on the backend.
export interface UserAuditEntry {
  id: string;
  created_at: string;
  action: string;
  actor_role: 'user' | 'admin' | 'system';
  actor_admin_id?: string;
  method?: string;
  path?: string;
  status?: number;
  ip?: string;
  user_agent?: string;
  // Anything the handler attached via WithDetails(), plus legacy
  // fields from earlier admin audit entries (e.g. {visible: true}).
  details?: Record<string, unknown>;
}

export interface UserAuditTrail {
  entries: UserAuditEntry[];
  total: number;
  page: number;
  page_size: number;
}

export const adminAPI = {
  // TOTP is optional: a not-yet-enrolled admin signs in with email +
  // password and receives an AdminEnrollmentChallenge instead of a
  // session (then completes via enrollTotp).
  //
  // Sign-in always targets PROD explicitly, regardless of which
  // environment is currently active in the toggle — there is only ever
  // one login, and it authenticates against prod (see lib/federation.ts).
  // enrollTotp completes that same prod challenge, so it must land on
  // the same deployment that issued it.
  signin: (body: { email: string; password: string; totp?: string }) =>
    request<AdminSigninResult>('/admin/auth/signin', { method: 'POST', body, base: ENVIRONMENTS.prod.apiBase }),
  // First-sign-in authenticator enrollment: confirm a code from the
  // freshly-scanned QR → enrolled + signed in (returns a session).
  enrollTotp: (body: { email: string; password: string; totp: string }) =>
    request<AdminSession>('/admin/auth/enroll-totp', { method: 'POST', body, base: ENVIRONMENTS.prod.apiBase }),
  // Explicit sign-out revokes every environment that currently holds a
  // token, not just the active one — the unified console can hold a live
  // dev session and a live prod session at once (see environments.ts),
  // and "sign out" means both go away. Each environment is posted to its
  // own apiBase with its own token (mirrors signin/enrollTotp, which
  // target ENVIRONMENTS.prod.apiBase explicitly rather than the
  // currently-active base).
  //
  // Best-effort per environment: the backend's /admin/auth/signout is
  // deliberately fail-open (an unverifiable token still returns 200), so
  // a revoke failure is silent server-side too. Callers still `.catch(()
  // => null)` this — local session state (adminSession.clearAll()) is
  // the reliable half and must be cleared regardless of what the network
  // says.
  signout: () =>
    Promise.all(
      (Object.keys(ENVIRONMENTS) as AdminEnv[]).map((env) => {
        const token = adminSession.getToken(env);
        if (!token) return Promise.resolve();
        return fetch(`${ENVIRONMENTS[env].apiBase}/admin/auth/signout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => null);
      }),
    ).then(() => undefined),
  // Fetches the PDF as a blob (so the Bearer header makes it through
  // — a plain <a download> can't set Authorization). Caller is
  // responsible for createObjectURL + cleanup. Returns the blob plus
  // the filename suggested by the server's Content-Disposition.
  downloadUserAuditTrailPDF: async (userID: string): Promise<{ blob: Blob; filename: string }> => {
    const token = adminSession.getToken();
    const res = await fetch(`${API_BASE}/admin/users/${userID}/audit-trail.pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      // Mirror the JSON-error shape from request() so the UI surfaces
      // the same friendlyError() string it does for the trail list.
      let code = 'UNKNOWN';
      let msg = 'Could not download PDF.';
      try {
        const body = await res.json();
        code = body?.error?.code ?? code;
        msg = body?.error?.message ?? msg;
      } catch { /* non-JSON body */ }
      throw new APIError(res.status, code, msg);
    }
    const blob = await res.blob();
    // Pull filename out of Content-Disposition: attachment; filename="…"
    const cd = res.headers.get('Content-Disposition') ?? '';
    const m = /filename="([^"]+)"/.exec(cd);
    const filename = m ? m[1] : `audit-${userID}.pdf`;
    return { blob, filename };
  },
};

export interface DMReport {
  id: string;
  reporter_user_id: string;
  reported_user_id: string;
  message_id?: string;
  // Populated server-side via LEFT JOIN dm_messages — surfaced to the
  // admin so they can read the offending text without a click-through.
  message_body?: string;
  reason: string;
  status: 'open' | 'reviewed' | 'dismissed';
  admin_notes?: string;
  resolved_at?: string;
  created_at: string;
}

export type AIModProvider = 'claude' | 'openai' | 'deepseek' | 'kimi' | 'minimax';

export interface AIModProviderStatus {
  id: AIModProvider;
  label: string;
  configured: boolean;
  active: boolean;
  model: string;
}

export interface AIModConfig {
  active: AIModProvider;
  providers: AIModProviderStatus[];
}
